<script setup lang="ts">
import { type ShallowRef, computed, shallowRef, watchEffect } from 'vue'
import { Primitive } from 'radix-vue'
import VIcon from '../VIcon/VIcon.vue'
import VSpinner from '../VSpinnerIcon/VSpinnerIcon.vue'
import { EButtonStates, type IButtonProps, type TButtonConfig, VButtonSizes, VButtonVariants } from './types'
import { useButtonConfig } from './composables/useButtonConfig'
import { isDark } from '@/lib/utils'
import { ICON_SIZES } from '@/ui/ts/types/Sizes'

const props = withDefaults(defineProps<IButtonProps>(), {
    as: 'button',
    variant: VButtonVariants.PRIMARY,
})

const safeSize = computed(() => props.size || VButtonSizes.SMALL)
const buttonConfig: ShallowRef<TButtonConfig> = shallowRef(useButtonConfig(props))

watchEffect(() => {
    buttonConfig.value = useButtonConfig(props)
})
</script>

<template>
    <Primitive
        :as="(to || href) && !disabled ? 'a' : as"
        :as-child="asChild"
        :class="[isDark(dark), buttonConfig.root]"
        :data-test="`button-${dataTest}`"
        :to="to"
        :href="href"
        :disabled="disabled || props.state === EButtonStates.LOADING"
        data-gpp-tools-ui
    >
        <div
            class="gap-horizontal-icon-to-element flex max-w-full items-center"
            :class="{ 'opacity-0': props.state === EButtonStates.LOADING }"
        >
            <VIcon
                v-if="leadingIcon"
                :name="leadingIcon"
                :size="ICON_SIZES[safeSize]"
            />

            <div class="truncate">
                <slot>
                    {{ label }}
                </slot>
            </div>

            <VIcon
                v-if="trailingIcon"
                :name="trailingIcon"
                :size="ICON_SIZES[safeSize]"
            />
        </div>

        <div
            v-if="props.state === EButtonStates.LOADING"
            class="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 align-top"
        >
            <VSpinner
                :id="`v-spinner-${to || href}`"
                size="l"
                data-test="spinner"
                :dark="dark ? dark : false"
            />
        </div>
    </Primitive>
</template>
